import React, { useEffect, useMemo, useState } from "react";
import { Heart, AlertTriangle, Stethoscope, Copy, ExternalLink, Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0E1B3A";
const GOLD = "#C9A24B";
const PURPLE = "#4A0E8F";

/**
 * Public, no-auth, embeddable ASCVD 10-year risk widget.
 * Lives at /heart-risk and at /embed/heart-risk (iframe-friendly chrome-less variant).
 */
export default function HeartRisk({ embed = false }) {
  const [f, setF] = useState({
    age: 55, sex: "M", race: "white", total_chol: 200,
    hdl: 50, sbp: 130, treated_htn: false, diabetes: false, smoker: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const u = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) });

  useEffect(() => {
    if (f.age < 20 || f.age > 100) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/api/public/ascvd`, f);
        setData(r.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [JSON.stringify(f)]);  // eslint-disable-line

  const colorByCat = useMemo(() => ({
    low: "#059669", borderline: "#C9A24B", intermediate: "#F59E0B", high: "#DC2626",
  }), []);

  return (
    <div className="min-h-screen w-full" style={{ background: embed ? "transparent" : "#F8F4FF" }}>
      {!embed && <PublicHeader />}
      <main className={`max-w-4xl mx-auto ${embed ? "p-4" : "px-4 sm:px-6 py-8 md:py-12"}`}>
        {!embed && (
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: NAVY }}>
              10-Year Heart-Attack &amp; Stroke Risk
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
              An evidence-based educational estimate using the ACC/AHA Pooled Cohort Equations.
              Validated for adults aged 40-79. Not a substitute for clinical evaluation.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* INPUTS */}
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: "#E2E8F0" }} data-testid="hr-inputs">
            <div className="text-sm font-semibold mb-3" style={{ color: NAVY }}>About you</div>
            <Field label="Age">
              <input type="number" value={f.age} onChange={u("age")} data-testid="hr-age" className="hr-input" />
            </Field>
            <Field label="Sex at birth">
              <select value={f.sex} onChange={u("sex")} data-testid="hr-sex" className="hr-input">
                <option value="M">Male</option><option value="F">Female</option>
              </select>
            </Field>
            <Field label="Race">
              <select value={f.race} onChange={u("race")} data-testid="hr-race" className="hr-input">
                <option value="white">White / Other</option>
                <option value="black">African American</option>
              </select>
            </Field>
            <Field label="Total cholesterol (mg/dL)">
              <input type="number" value={f.total_chol} onChange={u("total_chol")} data-testid="hr-tc" className="hr-input" />
            </Field>
            <Field label="HDL cholesterol (mg/dL)">
              <input type="number" value={f.hdl} onChange={u("hdl")} data-testid="hr-hdl" className="hr-input" />
            </Field>
            <Field label="Systolic blood pressure (mmHg)">
              <input type="number" value={f.sbp} onChange={u("sbp")} data-testid="hr-sbp" className="hr-input" />
            </Field>
            <div className="mt-3 space-y-1.5">
              {[["treated_htn","Currently on BP medication"],
                ["diabetes","Diabetes"],
                ["smoker","Current smoker"]].map(([k,l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f[k]} onChange={u(k)} data-testid={`hr-${k}`} className="w-4 h-4" />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* RESULT */}
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: "#E2E8F0" }} data-testid="hr-result">
            {loading && <div className="text-sm text-slate-500">Calculating…</div>}
            {!loading && data && data.risk_pct == null && (
              <div className="text-sm" data-testid="hr-not-valid">
                <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs font-semibold mb-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Outside calculator range
                </div>
                <p className="text-slate-700">{data.note}</p>
              </div>
            )}
            {!loading && data && data.risk_pct != null && (
              <>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Estimated 10-year risk</div>
                <div className="flex items-baseline gap-3">
                  <div className="text-5xl font-semibold" style={{ color: colorByCat[data.category] || NAVY }} data-testid="hr-pct">
                    {data.risk_pct}%
                  </div>
                  <span className="px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold"
                    style={{ background: `${colorByCat[data.category]}15`, color: colorByCat[data.category], borderColor: `${colorByCat[data.category]}55` }}
                    data-testid="hr-category">
                    {data.category}
                  </span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border" style={{ background: NAVY, color: GOLD, borderColor: NAVY }}>
                  {data.guideline_badge}
                </div>
                <div className="mt-4 text-sm leading-relaxed text-slate-700 bg-slate-50 rounded-md p-3" data-testid="hr-guidance">
                  {data.guidance}
                </div>
                <div className="mt-4 text-[11px] italic text-slate-500" data-testid="hr-citation">
                  <strong className="not-italic">Citation:</strong> {data.citation}
                </div>
                <div className="mt-2 text-[11px] text-amber-800 italic" data-testid="hr-disclaimer">
                  {data.disclaimer}
                </div>
                <EmailReport data={data} inputs={f} />
              </>
            )}
          </div>
        </div>

        {!embed && <EmbedSnippet />}
        {!embed && <PublicFooter />}
      </main>

      {embed && (
        <div className="px-4 py-2 text-[10px] text-slate-500 border-t border-slate-200 text-center" data-testid="hr-embed-footer">
          Powered by <a href={`${window.location.origin}/heart-risk`} target="_blank" rel="noreferrer" className="underline" style={{ color: PURPLE }}>ZidaMD</a> · CamelionAI LLC · Patent Pending USPTO #64/078,696
        </div>
      )}

      <style>{`
        .hr-input { width:100%; padding:10px 12px; border:1px solid #E2E8F0; border-radius:8px;
                    font-size:16px; background:white; outline:none; }
        .hr-input:focus { border-color: ${NAVY}; box-shadow: 0 0 0 3px ${NAVY}22; }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-2.5">
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

function PublicHeader() {
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PURPLE }}>
            <Stethoscope className="w-5 h-5 text-white" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight" style={{ color: PURPLE }}>ZidaMD</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">by CamelionAI LLC</div>
          </div>
        </a>
        <a href="/login" className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5 text-slate-700" data-testid="hr-clinician-cta">
          For Clinicians <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </header>
  );
}


function EmailReport({ data, inputs }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (data?.risk_pct == null) return null;

  const reportText = () => `ZIDAMD — 10-YEAR HEART-ATTACK & STROKE RISK REPORT
============================================================

ESTIMATED 10-YEAR ASCVD RISK: ${data.risk_pct}% (${(data.category || "").toUpperCase()})

GUIDELINE: ${data.guideline_badge}
GUIDANCE:  ${data.guidance}

YOUR INPUTS
- Age: ${inputs.age}
- Sex at birth: ${inputs.sex === "F" ? "Female" : "Male"}
- Race: ${inputs.race === "black" ? "African American" : "White / Other"}
- Total cholesterol: ${inputs.total_chol} mg/dL
- HDL cholesterol: ${inputs.hdl} mg/dL
- Systolic blood pressure: ${inputs.sbp} mmHg
- On BP medication: ${inputs.treated_htn ? "Yes" : "No"}
- Diabetes: ${inputs.diabetes ? "Yes" : "No"}
- Current smoker: ${inputs.smoker ? "Yes" : "No"}

CITATION
${data.citation}

DISCLAIMER
${data.disclaimer}

————————————————————————————————————————
ZidaMD by CamelionAI LLC — Boca Raton, FL
Patent Pending USPTO #64/078,696 | #64/078,734 | #64/062,147
Visit your clinician portal: ${typeof window !== "undefined" ? window.location.origin : ""}/login`;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email"); return;
    }
    setSending(true);
    try {
      // 1. Lead capture (no PHI — only risk band + email)
      await axios.post(`${API}/api/public/leads`, {
        email, source: "heart-risk-widget",
        risk_pct: data.risk_pct, category: data.category,
        age: inputs.age, sex: inputs.sex, consent_marketing: consent,
      });
      // 2. Mailto drop-in (v1) — opens user's default mail client with the full
      //    report pre-filled in the body. No SMTP, no attachment, no PHI stored.
      const subject = encodeURIComponent(`Your ZidaMD 10-Year Heart-Risk Report (${data.risk_pct}% — ${data.category})`);
      const body = encodeURIComponent(reportText());
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      setSent(true);
      toast.success("Your email app opened with the report — press send");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't process — please try again");
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="mt-4 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm flex items-start gap-2" data-testid="hr-email-sent">
        <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-emerald-900">Email opened</div>
          <div className="text-emerald-800 text-xs leading-relaxed">
            Your default email app opened with the full risk summary pre-filled in the body. Just press send to email it to yourself for your records.
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 pt-3 border-t border-slate-200" data-testid="hr-email-block">
      <div className="flex items-center gap-2 mb-2 text-xs font-semibold" style={{ color: NAVY }}>
        <Mail className="w-3.5 h-3.5" /> Email me my risk summary
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          required placeholder="you@example.com"
          className="hr-input flex-1" data-testid="hr-email-input" autoComplete="email" inputMode="email" />
        <button type="submit" disabled={sending}
          className="px-4 py-2.5 rounded-md text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: PURPLE, minHeight: 44 }}
          data-testid="hr-email-submit">
          <Send className="w-3.5 h-3.5" /> {sending ? "Preparing…" : "Send to me"}
        </button>
      </div>
      <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600" data-testid="hr-consent-label">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-3.5 h-3.5" data-testid="hr-consent" />
        I'd like occasional ZidaMD updates (optional — uncheck for one-time report only).
      </label>
      <div className="mt-2 text-[10px] text-slate-500 italic">
        v1: opens your default email client with the full risk summary in the body — no PHI is stored on our servers.
      </div>
    </form>
  );
}

function PublicFooter() {
  return (
    <div className="mt-10 pt-6 border-t border-slate-200 text-center text-[11px] text-slate-500 space-y-1">
      <div className="font-medium text-slate-700">ZidaMD by CamelionAI LLC — Boca Raton, FL</div>
      <div>Patent Pending USPTO #64/078,696 | #64/078,734 | #64/062,147</div>
      <div className="text-amber-800 italic max-w-2xl mx-auto pt-2">
        This calculator provides educational information only and does not constitute medical advice. Always consult a qualified clinician about your individual cardiovascular risk.
      </div>
    </div>
  );
}

function EmbedSnippet() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://app.zidamd.com";
  const code = `<iframe src="${origin}/embed/heart-risk" width="100%" height="640" frameborder="0" style="border:0;border-radius:12px" loading="lazy" title="ZidaMD Heart Risk Widget"></iframe>`;
  return (
    <div className="mt-10 bg-white border border-slate-200 rounded-xl p-5" data-testid="hr-embed-block">
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-4 h-4" style={{ color: PURPLE }} />
        <div className="text-sm font-semibold" style={{ color: NAVY }}>Embed this widget</div>
      </div>
      <p className="text-xs text-slate-600 mb-3 leading-relaxed">
        Paste this snippet into any web page (your practice site, patient portal, marketing landing) — fully responsive, no login required, ZidaMD-branded.
      </p>
      <div className="relative">
        <pre className="text-[11px] bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto font-mono" data-testid="hr-embed-snippet">{code}</pre>
        <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Embed code copied"); }}
          className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-1"
          data-testid="hr-embed-copy">
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
    </div>
  );
}
